import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { officeListQuotes } from "@/lib/office.functions";
import { FileText, Plus, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/_employee/medewerker/office/offertes/")({
  head: () => ({ meta: [{ title: "Offertes — EVG Office" }, { name: "robots", content: "noindex" }] }),
  component: QuotesList,
});

const STATUS = [
  { id: "all", label: "Alle" },
  { id: "draft", label: "Concept" },
  { id: "sent", label: "Verzonden" },
  { id: "signed", label: "Getekend" },
  { id: "declined", label: "Afgewezen" },
];

function fmt(cents: number) {
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(cents / 100);
}

function QuotesList() {
  const [status, setStatus] = useState("all");
  const list = useServerFn(officeListQuotes);
  const { data, isLoading } = useQuery({ queryKey: ["office-quotes", status], queryFn: () => list({ data: { status } }) });
  const quotes = data?.quotes ?? [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Offertes</h1>
          <p className="text-sm text-slate-500">{quotes.length} offertes</p>
        </div>
        <Link to="/medewerker/office/offertes/nieuw" className="inline-flex items-center gap-2 rounded-lg bg-[#0b2a4a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0e3463]">
          <Plus className="size-4" /> Nieuwe offerte
        </Link>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {STATUS.map((s) => (
          <button key={s.id} onClick={() => setStatus(s.id)} className={`rounded-full px-3 py-1 text-xs font-semibold transition ${status === s.id ? "bg-[#0b2a4a] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100"}`}>{s.label}</button>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="p-3 text-left font-semibold">Nummer</th>
              <th className="p-3 text-left font-semibold">Titel</th>
              <th className="p-3 text-left font-semibold">Betaalplan</th>
              <th className="p-3 text-right font-semibold">Totaal</th>
              <th className="p-3 text-left font-semibold">Status</th>
              <th className="p-3 text-left font-semibold">Datum</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">Laden…</td></tr>
            ) : quotes.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">Nog geen offertes</td></tr>
            ) : quotes.map((q: any) => (
              <tr key={q.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="p-3 font-mono text-xs">{q.quote_number}</td>
                <td className="p-3 font-medium">{q.title}</td>
                <td className="p-3 text-xs text-slate-500">{q.payment_plan ?? "—"}</td>
                <td className="p-3 text-right font-semibold">{fmt(q.total_cents)}</td>
                <td className="p-3">
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs">
                    {q.status === "signed" && <CheckCircle2 className="size-3 text-emerald-600" />}
                    {q.status}
                  </span>
                </td>
                <td className="p-3 text-xs text-slate-500">{new Date(q.created_at).toLocaleDateString("nl-NL")}</td>
                <td className="p-3 text-right">
                  <Link to="/medewerker/office/offertes/$id" params={{ id: q.id }} className="text-xs font-semibold text-[#0b2a4a] hover:underline">Openen →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
